// src/slices/ticketDelivery/thunk.ts
// Async thunks cho phân hệ Giao vé cho đại lý (mục 4.7, README_TICKETHANDLE.md)

import { createAsyncThunk } from '@reduxjs/toolkit';
import {
  getTicketDeliveriesApi,
  getTicketDeliveryByIdApi,
  createTicketDeliveryApi,
  updateTicketDeliveryApi,
  confirmTicketDeliveryApi,
  cancelTicketDeliveryApi,
  agentConfirmTicketDeliveryApi,
  getTicketStockBalancesApi,
} from 'helpers/fakebackend_helper';
import { CreateTicketDeliveryPayload } from 'pages/TicketDeliveryManagement/types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_messages');

interface FetchTicketDeliveriesParams {
  pageIndex?: number;
  pageSize?: number;
  planId?: number | string;
  agentId?: number | string;
  status?: number | string;
}

// Loại bỏ param rỗng để không gửi query thừa lên API Gateway
const cleanParams = (params: any): any => {
  const cleaned: any = {};
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== null && value !== undefined && value !== '') {
      cleaned[key] = value;
    }
  });
  return cleaned;
};

// Danh sách phiếu giao vé
export const fetchTicketDeliveries = createAsyncThunk(
  'TicketDelivery/fetchList',
  async (params: FetchTicketDeliveriesParams, { rejectWithValue }) => {
    try {
      const response = await getTicketDeliveriesApi(cleanParams(params));
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_danh_sach_phieu_message'));
    }
  },
);

// Chi tiết 1 phiếu giao
export const fetchTicketDeliveryById = createAsyncThunk(
  'TicketDelivery/fetchById',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await getTicketDeliveryByIdApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_chi_tiet_phieu_message'));
    }
  },
);

// Tồn kho tổng công ty theo Symbol của 1 kế hoạch phát hành
export const fetchTicketStockBalances = createAsyncThunk(
  'TicketDelivery/fetchStockBalances',
  async (params: { planId: number; agentId?: number }, { rejectWithValue }) => {
    try {
      const response = await getTicketStockBalancesApi(cleanParams(params));
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tai_ton_kho_ve_message'));
    }
  },
);

// Tạo phiếu giao (Draft)
export const createTicketDelivery = createAsyncThunk(
  'TicketDelivery/create',
  async (data: CreateTicketDeliveryPayload, { rejectWithValue }) => {
    try {
      const response = await createTicketDeliveryApi(data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_tao_phieu_giao_ve_message'));
    }
  },
);

// Sửa các dòng của phiếu khi còn Draft
export const updateTicketDelivery = createAsyncThunk(
  'TicketDelivery/update',
  async (
    { id, data }: { id: number; data: Partial<CreateTicketDeliveryPayload> },
    { rejectWithValue },
  ) => {
    try {
      const response = await updateTicketDeliveryApi(id, data);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_cap_nhat_phieu_giao_message'));
    }
  },
);

// Xác nhận giao vé: Draft -> Confirmed
export const confirmTicketDelivery = createAsyncThunk(
  'TicketDelivery/confirm',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await confirmTicketDeliveryApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_xac_nhan_phieu_giao_message'));
    }
  },
);

// Huỷ phiếu khi còn Draft
export const cancelTicketDelivery = createAsyncThunk(
  'TicketDelivery/cancel',
  async ({ id, reason }: { id: number; reason?: string }, { rejectWithValue }) => {
    try {
      const response = await cancelTicketDeliveryApi(id, reason);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_huy_phieu_giao_ve_message'));
    }
  },
);

// Đại lý xác nhận đã nhận vé: Confirmed -> AgentConfirmed (mục 13.2)
export const agentConfirmTicketDelivery = createAsyncThunk(
  'TicketDelivery/agentConfirm',
  async (id: number, { rejectWithValue }) => {
    try {
      const response = await agentConfirmTicketDeliveryApi(id);
      return response.data || response;
    } catch (error: any) {
      return rejectWithValue(error?.message || st('khong_the_xac_nhan_dai_ly_message'));
    }
  },
);
